import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaskitoOptions } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';
import { AsyncPipe } from '@angular/common';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { FormattedDate } from '../../../models/shared/formatted-date';
import { distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import * as moment from 'moment';
import { DateSelection } from './datepicker-input-calendar/datepicker-input-calendar.component';
import { DatepickerInputCalendarComponent } from './datepicker-input-calendar/datepicker-input-calendar.component';
import { FormValidationUtils } from '../../../utils/form-validation-utils';

@Component({
  selector: 'app-datepicker-input',
  templateUrl: './datepicker-input.component.html',
  styleUrls: ['./datepicker-input.component.scss'],
  standalone: true,
  imports: [AsyncPipe, MaskitoDirective, NgbPopover, DatepickerInputCalendarComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class DatepickerInputComponent implements ControlValueAccessor, OnChanges, OnInit, AfterViewInit, OnDestroy {
  @Input() placeholder: string = '';
  @Input() controlId: string | null = null;
  @Input() useDateRange: boolean = false;
  @Input() isStartDate: boolean = true;
  @Input() partnerControl!: FormControl;
  @Input() minDate: string = '';
  @Input() maxDate: string = '';
  @Input() error: boolean = false;

  @Output() inputFocused = new EventEmitter<void>();

  @ViewChild('popover') popover!: NgbPopover;
  @ViewChild('nativeDateInput') nativeDateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('customDateInput') customDateInput!: ElementRef<HTMLInputElement>;

  private elementRef = inject(ElementRef);

  public today = moment().format('YYYY-MM-DD').toString();

  public disabled: boolean = false;
  private resizeObserver!: ResizeObserver;

  public maskitoOptions: MaskitoOptions = {
    mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]
  };

  // Touch devices get the OS date picker; anything with a mouse gets the calendar popover, however
  // narrow the window is (split-screen desktops are well under any width-based mobile breakpoint).
  private readonly useNativeDatePicker = matchMedia('(pointer: coarse)').matches;

  private _disabled = new BehaviorSubject<boolean>(false);
  public disabled$ = this._disabled as Observable<boolean>;

  private _useAbbreviatedDate = new BehaviorSubject<boolean>(false);
  public useAbbreviatedDate$ = this._useAbbreviatedDate as Observable<boolean>;

  private _showingDatePicker = new BehaviorSubject<boolean>(false);
  public showingDatePicker$ = this._showingDatePicker as Observable<boolean>;

  private _partnerControl = new BehaviorSubject<FormControl | null>(null);
  public partnerControl$ = this._partnerControl as Observable<FormControl | null>;

  private _isUsingRange = new BehaviorSubject<boolean>(false);
  public isUsingRange$ = this._isUsingRange as Observable<boolean>;

  private _isStartDate = new BehaviorSubject<boolean>(true);
  public isStartDate$ = this._isStartDate as Observable<boolean>;

  private _isFocused = new BehaviorSubject<boolean>(false);
  public isFocused$ = this._isFocused as Observable<boolean>;

  public _displayErrorStyles = new BehaviorSubject<boolean>(false);
  public displayErrorStyles$ = this._displayErrorStyles as Observable<boolean>;

  private _relevantDate = new BehaviorSubject<FormattedDate | null>(null); // universal truth of the relevant date
  public relevantDate$ = this._relevantDate.asObservable();

  private _minDate = new BehaviorSubject<string | null>(null);
  public minDate$ = this._minDate.asObservable();

  private _maxDate = new BehaviorSubject<string | null>(null);
  public maxDate$ = this._maxDate.asObservable();

  public selectedFormattedDates$ = combineLatest([
    this.isStartDate$,
    this.relevantDate$,
    this.isUsingRange$,
    this.partnerControl$.pipe(switchMap(control => control?.valueChanges.pipe(startWith(control?.value)) ?? of(null)))
  ]).pipe(
    map(([isStartDate, relevantDate, isUsingRange, partnerControlValue]) => {
      const partnerDate = isUsingRange ? FormattedDate.fromUtcString(partnerControlValue) : null;
      return {
        startDate: isStartDate ? relevantDate : partnerDate,
        endDate: isStartDate ? partnerDate : relevantDate
      } as DateSelection;
    }),
    // The sources re-emit the same dates as new object references (e.g. on every writeValue), so
    // only pass real changes through — the calendar rebuilds its view state on each emission.
    distinctUntilChanged(
      (previous, current) =>
        previous.startDate?.utcString === current.startDate?.utcString &&
        previous.endDate?.utcString === current.endDate?.utcString
    )
  );

  // Formats the relevant date for display in the input field
  public displayValue$ = combineLatest([
    this.relevantDate$,
    this.showingDatePicker$,
    this.isFocused$,
    this.useAbbreviatedDate$
  ]).pipe(
    map(([relevantDate, showingDatepicker, isFocused, useAbbreviatedDate]) => {
      if (!relevantDate) return '';

      if (showingDatepicker || isFocused) {
        return relevantDate.shortHandDate;
      }

      if (!relevantDate.isValid) {
        return relevantDate.shortHandDate;
      }
      return useAbbreviatedDate ? relevantDate.abbreviated : relevantDate.longFormDate;
    })
  );

  public nativeInputValue$ = this.relevantDate$.pipe(
    map(relevantDate => {
      if (!relevantDate) return '';
      return relevantDate.nativeDateInput;
    })
  );

  public inputIconSrc$ = combineLatest([
    this.isFocused$,
    this.showingDatePicker$,
    this.displayErrorStyles$,
    this.disabled$
  ]).pipe(
    map(([isFocused, showingDatePicker, displayErrorStyles, disabled]) => {
      if (displayErrorStyles) {
        return 'assets/icons/red/Calendar.svg';
      }
      if (disabled) {
        return 'assets/icons/disabled-input/Calendar.svg';
      }
      if (showingDatePicker) {
        return 'assets/icons/dark/Calendar.svg';
      }
      return isFocused ? 'assets/icons/dark/Calendar.svg' : 'assets/icons/grey/Calendar.svg';
    })
  );

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.useDateRange) {
      this._isUsingRange.next(this.useDateRange);
    }
    if (changes.partnerControl) {
      this._partnerControl.next(this.partnerControl);
    }
    if (changes.isStartDate) {
      this._isStartDate.next(this.isStartDate);
    }
    if (changes.error) {
      this._displayErrorStyles.next(this.error);
    }
    if (changes.minDate) {
      this._minDate.next(this.minDate);
    }
    if (changes.maxDate) {
      this._maxDate.next(this.maxDate);
    }
  }

  public ngOnInit(): void {
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        this._useAbbreviatedDate.next(width < 180);
      }
    });
  }

  public ngAfterViewInit(): void {
    this.resizeObserver.observe(this.customDateInput.nativeElement);
  }

  public ngOnDestroy(): void {
    this.resizeObserver.disconnect();
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:focusin', ['$event'])
  public onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    // A detached target means the clicked element was removed by a re-render before the event
    // bubbled up to the document (e.g. a menu option whose list was rebuilt by its own click
    // handler). Something we rendered can't be an outside click, so never close on it.
    if (!target.isConnected) {
      return;
    }
    // The calendar's month/year dropdown menus are portaled to <body>, outside this component —
    // treat clicks inside them as "inside" too, or opening a menu would close the whole calendar.
    if (
      this.elementRef.nativeElement.contains(target) ||
      target.closest('app-datepicker-input-calendar') ||
      target.closest('.date-picker-nav-menu')
    ) {
      return;
    }
    this.popover?.close();
    this._isFocused.next(false);
    this._showingDatePicker.next(false);
  }

  // Programmatic update: update display immediately, update service, but do not emit onChange.
  public writeValue(value: string | null): void {
    if (!value) {
      this._relevantDate.next(null);
      return;
    }
    let selectedDate: FormattedDate;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      selectedDate = FormattedDate.fromNativeDateInput(value);
    } else {
      selectedDate = FormattedDate.fromUtcString(value);
    }
    this._relevantDate.next(selectedDate);
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._disabled.next(isDisabled);
  }

  public onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (!value?.trim()) {
      this._relevantDate.next(null);
      this.onChange(null);
      return;
    }
    const selectedDate = FormattedDate.fromShorthand(value);
    this._relevantDate.next(selectedDate);
    this.onChange(selectedDate.isValid ? selectedDate.nativeDateInput : FormValidationUtils.INVALID_DATE_MARKER);
  }

  public onFocus(): void {
    this._isFocused.next(true);
    this.openDatePicker();
    this.inputFocused.emit();
  }

  public onBlur(): void {
    this._isFocused.next(false);
  }

  // User interaction: derive relevant date and emit onChange
  public onDateSelected(dateSelection: DateSelection): void {
    const relevantDate = this.isStartDate ? dateSelection.startDate : dateSelection.endDate;
    const partnerDate = this.isStartDate ? dateSelection.endDate : dateSelection.startDate;
    this._relevantDate.next(relevantDate ?? null);
    this.onChange(relevantDate?.nativeDateInput ?? null);

    if (this.useDateRange) {
      this.partnerControl?.setValue(partnerDate?.nativeDateInput ?? null);
    }
    this.popover.close();
    this._showingDatePicker.next(false);
    this._isFocused.next(false);
  }

  public openDatePicker(): void {
    if (this.disabled) return;
    this._showingDatePicker.next(true);
    this.customDateInput.nativeElement.focus();
    if (this.useNativeDatePicker) {
      this.showMobileNativeDatePicker();
    } else {
      this.popover.open();
    }
  }

  public showMobileNativeDatePicker(): void {
    this.nativeDateInput.nativeElement.click(); // Show calendar picker on Android devices
    this.nativeDateInput.nativeElement.focus(); // Show calendar picker on iOS devices
    this.nativeDateInput.nativeElement.showPicker(); // Show calendar picker on other devices
  }

  public onNativeInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target.value) {
      this._relevantDate.next(null);
      this.onChange(null);
    } else {
      const formattedDate = FormattedDate.fromNativeDateInput(target.value);
      this._relevantDate.next(formattedDate);
      this.onChange(formattedDate.isValid ? formattedDate.nativeDateInput : FormValidationUtils.INVALID_DATE_MARKER);
    }
    this._showingDatePicker.next(false);
    this._isFocused.next(false);
  }
}
